import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Trash2, Check, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Brand from "@/components/Brand";

// Public account-deletion request page. Required by Google Play policy —
// users must be able to request deletion of their account & data WITHOUT
// installing the app (in case they've already uninstalled).
//
// Flow: user enters email → server records the request in
// `db.deletion_requests` → admin (or an automated job) verifies + hard-
// deletes within 30 days per DPDPA §12.

export default function DeleteAccount() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!confirm) {
      toast.error("Please confirm you understand this is permanent.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/account/deletion-request", {
        email: email.trim().toLowerCase(),
        reason: reason.trim() || null,
      });
      setDone(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit request.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div
        className="min-h-[100dvh] flex flex-col items-center justify-center px-6 text-center bg-background"
        style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="delete-account-done"
      >
        <div className="h-14 w-14 border border-accent rounded-full flex items-center justify-center text-accent">
          <Check size={28} strokeWidth={1.5} />
        </div>
        <h1 className="editorial-h2 mt-6">Request received.</h1>
        <p className="text-sm text-muted-foreground mt-3 max-w-sm">
          Reference: <span className="font-mono">{done.request_id}</span>
        </p>
        <p className="text-xs text-muted-foreground mt-4 max-w-md leading-relaxed">
          We will verify this request by emailing <b>{email}</b> and then
          permanently delete your account and all associated data
          (photos, outfits, scans, chats, subscriptions) within <b>30 days</b>.
          Payment records may be retained for 8 years as required by Indian
          tax law, with all identifying information removed.
        </p>
        <Button
          onClick={() => navigate("/")}
          className="mt-8 rounded-none h-11 px-8 uppercase tracking-[0.2em] text-xs"
          data-testid="delete-account-back-btn"
        >
          Back
        </Button>
      </div>
    );
  }

  return (
    <div
      className="min-h-[100dvh] bg-background text-foreground"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="delete-account-page"
    >
      <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-2xl mx-auto px-6 h-14 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em]" data-testid="delete-account-back-nav">
            <ArrowLeft size={14} /> Back
          </button>
          <Brand size="sm" />
          <span className="w-16" />
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-10 lg:py-16">
        <p className="eyebrow flex items-center gap-2"><Trash2 size={12} /> Account deletion</p>
        <h1 className="editorial-h1 mt-3">Delete your StyleBay account.</h1>
        <p className="text-sm text-muted-foreground mt-3 max-w-md">
          Submit the form below. We'll verify by email and permanently
          delete your account within 30 days.
        </p>

        <div className="mt-8 border border-destructive/40 bg-destructive/5 p-4 flex gap-3" data-testid="delete-account-warning">
          <AlertTriangle className="text-destructive shrink-0" size={18} />
          <div className="text-xs text-muted-foreground leading-relaxed">
            <p><b className="text-destructive">This action is permanent.</b> After the 30-day
            verification window, the following will be irreversibly deleted:</p>
            <ul className="list-disc pl-4 mt-2 space-y-0.5">
              <li>Profile info (name, bio, photos, style DNA)</li>
              <li>Wardrobe items, AI scans, outfit history, try-on images</li>
              <li>Social posts, comments, likes, follows</li>
              <li>Chat history with the AI stylist</li>
              <li>Active subscriptions (they will be cancelled)</li>
            </ul>
            <p className="mt-2">Payment records are retained for 8 years per Indian tax law with all personal identifiers removed.</p>
          </div>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-6" data-testid="delete-account-form">
          <div>
            <Label htmlFor="del-email" className="eyebrow">Account email</Label>
            <Input
              id="del-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-none mt-2 h-11"
              placeholder="you@gmail.com"
              data-testid="delete-account-email-input"
              required
            />
            <p className="text-[10px] text-muted-foreground mt-1.5">
              The email you use to sign in with Google.
            </p>
          </div>

          <div>
            <Label htmlFor="del-reason" className="eyebrow">Reason (optional)</Label>
            <Textarea
              id="del-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={500}
              className="rounded-none mt-2"
              placeholder="Help us improve — why are you leaving?"
              data-testid="delete-account-reason-input"
            />
          </div>

          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={confirm}
              onChange={(e) => setConfirm(e.target.checked)}
              data-testid="delete-account-confirm-checkbox"
              className="mt-1 h-4 w-4 accent-destructive flex-shrink-0"
            />
            <span className="text-xs leading-relaxed">
              I understand that my StyleBay account and all associated data
              will be permanently deleted within 30 days. This cannot be undone.
            </span>
          </label>

          <Button
            type="submit"
            disabled={busy || !confirm}
            data-testid="delete-account-submit-btn"
            className="w-full h-12 rounded-none uppercase tracking-[0.22em] text-xs bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
          >
            {busy ? <><Loader2 size={14} className="animate-spin mr-2" /> Submitting</> : "Request account deletion"}
          </Button>

          <p className="text-[10px] text-muted-foreground uppercase tracking-[0.18em] text-center">
            Prefer to talk first? <Link to="/grievance" className="underline">Contact our grievance officer</Link>.
          </p>
        </form>
      </div>
    </div>
  );
}
